import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import { addHours, addMinutes, format, isBefore, isValid, isAfter } from 'date-fns';

import { usePrevious } from '../../../../core/hooks/usePrevious';
import { getDefaultDate, getTimeDate } from '../../../../core/utils/date';
import { DATE_FORMAT_HOURS_MINUTES } from '../../../../core/constants/date';

import './TimePicker.scss';

interface Props {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: Date) => any;
  onFocus?: () => any;
  onBlur?: () => any;
  disableBefore?: string;
  disableAfter?: string;
  disabled?: boolean;
  excludeDisablingHours?: boolean;
}

export const generateTimeOptions = (disableBefore?: string, disableAfter?: string, excludeDisablingHours = false, minutesStep = 30) => {
  const options: { date: Date; isDisabled?: boolean }[] = [];
  const date = getDefaultDate();
  const disabledDateBefore = getTimeDate(disableBefore);
  const disabledDateAfter = getTimeDate(disableAfter);
  const isDisabledDate = (dateOption: any) => isBefore(dateOption, disabledDateBefore) || isAfter(dateOption, disabledDateAfter);
  const checkIsDisabledDateIfExist = (dateOption: any) => (disableBefore || disableAfter ? isDisabledDate(dateOption) : false);
  for (let i = 0; i < 24; i += 1) {
    const option = addHours(date, i);
    for (let j = 0; j < 60; j += minutesStep) {
      const dateOption = addMinutes(option, j);
      const isDisabled = excludeDisablingHours ? false : checkIsDisabledDateIfExist(dateOption);
      options.push({ date: dateOption, isDisabled });
    }
  }
  return options;
};

const TimePicker: React.FC<Props> = ({
  id,
  name,
  value,
  onChange,
  placeholder,
  onBlur,
  onFocus,
  disableBefore,
  disableAfter,
  disabled,
  excludeDisablingHours = false
}) => {
  const overlayRef = useRef<HTMLUListElement>(null);
  const options = generateTimeOptions(disableBefore, disableAfter, excludeDisablingHours);
  const [show, setShow] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string>(value ?? '');
  const target = useRef(null);

  const prevValue = usePrevious(value);

  useEffect(() => {
    if (value && prevValue !== value) {
      setSelectedTime(value);
    }
  }, [prevValue, value]);

  useLayoutEffect(() => {
    if (show && overlayRef.current) {
      const overlayList = overlayRef.current;
      const selectedItem = overlayList.querySelector('.time-picker__button--is-selected');

      if (selectedItem) {
        selectedItem?.scrollIntoView();
      }
    }
  }, [show]);

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const helperDate = getTimeDate(val);

    if (onBlur) {
      onBlur();
    }

    if (onChange && isValid(helperDate)) {
      const selectedOption = options.find(
        (option) => format(option.date, DATE_FORMAT_HOURS_MINUTES) === format(helperDate, DATE_FORMAT_HOURS_MINUTES)
      );
      if (selectedOption) {
        setSelectedTime(format(selectedOption.date, DATE_FORMAT_HOURS_MINUTES));
        onChange(selectedOption.date);
      } else {
        setSelectedTime('');
        onChange(getDefaultDate());
      }
    } else {
      setSelectedTime('');
    }
    setShow(false);
  };

  return (
    <div className="time-picker">
      <input
        className={`time-picker__input ${disabled ? 'time-picker__input--disabled' : ''}`}
        id={id}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        value={selectedTime}
        onFocus={(e) => {
          e.target.select();
          if (onFocus) {
            onFocus();
          }
          setShow(true);
        }}
        ref={target}
        onChange={(e) => {
          if (e.target.value.length > 5) {
            return;
          }
          setSelectedTime(e.target.value.replace(/[^0-9:]/g, ''));
        }}
        onBlur={handleOnBlur}
      />
      <Overlay target={target.current} show={show} placement="bottom-start">
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {({ placement, arrowProps, show: _show, popper, ...props }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div className="time-picker__dropdown-wrapper" {...props}>
            <ul className="time-picker__list" ref={overlayRef}>
              {options.map((item) => {
                const isSelected = format(item.date, DATE_FORMAT_HOURS_MINUTES) === selectedTime;
                const { date: selectedDate, isDisabled } = item;
                return (
                  <li key={String(item.date)} className="time-picker__item">
                    <button
                      className={`time-picker__button ${isSelected ? 'time-picker__button--is-selected' : ''} `}
                      disabled={isDisabled}
                      onClick={() => {
                        setSelectedTime(format(selectedDate, DATE_FORMAT_HOURS_MINUTES));
                        if (onChange) {
                          onChange(selectedDate);
                        }
                        setShow(false);
                      }}
                    >
                      {format(selectedDate, DATE_FORMAT_HOURS_MINUTES)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Overlay>
    </div>
  );
};

export default TimePicker;
